import { Link } from 'react-router-dom';
import { Button } from '@ui/components/button';
import { AppCard } from '@ui/components/layouts';
import {
  landingFeaturesAvailable,
  landingFeaturesUpcoming,
  landingText,
  type LandingFeature,
} from '@/src/lib/landing_text';
import { FaIcon } from '@micks-yard/icons/FaIcon';
import { faCalendarDays } from '@micks-yard/icons/solid/faCalendarDays';
import { faDroplet } from '@micks-yard/icons/solid/faDroplet';
import { faLeaf } from '@micks-yard/icons/solid/faLeaf';
import { faSeedling } from '@micks-yard/icons/solid/faSeedling';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

const availableIcons: IconDefinition[] = [faSeedling, faLeaf];
const upcomingIcons: IconDefinition[] = [faCalendarDays, faDroplet];
const SHOW_LANDING = false;

function FeatureCard({
  feature,
  icon,
  upcoming = false,
}: {
  feature: LandingFeature;
  icon: IconDefinition;
  upcoming?: boolean;
}) {
  return (
    <AppCard
      className={`flex min-w-0 flex-1 basis-full flex-col gap-4 p-5 md:basis-[calc(50%-0.5rem)] ${
        upcoming ? 'opacity-85' : ''
      }`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-1 items-center gap-3">
          <div
            className={`flex size-10 items-center justify-center rounded-full ${
              upcoming ? 'bg-muted text-muted-foreground' : 'bg-green-100 text-green-800'
            }`}>
            <FaIcon icon={icon} className="size-5" />
          </div>
          <span className="text-lg font-semibold">{feature.title}</span>
        </div>
        {upcoming ? (
          <span className="border-border bg-muted text-muted-foreground rounded-full border px-2 py-0.5 text-xs font-medium">
            {landingText.comingSoonBadge}
          </span>
        ) : null}
      </div>

      <p className="text-muted-foreground text-base leading-relaxed">{feature.description}</p>

      {feature.sections ? (
        <div className="flex flex-col gap-4">
          {feature.sections.map((section) => (
            <div key={section.title} className="flex flex-col gap-2">
              <span className="text-sm font-semibold">{section.title}</span>
              <ul className="flex flex-col gap-2 pl-1">
                {section.items.map((item) => (
                  <li
                    key={item}
                    className="text-muted-foreground flex gap-2 text-sm leading-relaxed">
                    <span className={upcoming ? 'text-muted-foreground' : 'text-green-600'}>•</span>
                    <span className="flex-1">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : (
        <ul className="flex flex-col gap-2 pl-1">
          {feature.items?.map((item) => (
            <li key={item} className="text-muted-foreground flex gap-2 text-sm leading-relaxed">
              <span className={upcoming ? 'text-muted-foreground' : 'text-green-600'}>•</span>
              <span className="flex-1">{item}</span>
            </li>
          ))}
        </ul>
      )}
    </AppCard>
  );
}

export function LandingScreen() {
  if (!SHOW_LANDING) {
    return null;
  }

  return (
    <div className="bg-background flex w-full flex-1 flex-col px-6 py-12">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-10">
        <div className="flex flex-col items-start gap-6">
          <span className="rounded-full border border-green-300 bg-green-50 px-3 py-0.5 text-xs font-medium text-green-800">
            {landingText.betaBadge}
          </span>

          <div className="flex flex-col gap-4">
            <h1 className="text-4xl font-semibold tracking-tight">{landingText.headline}</h1>
            <p className="text-muted-foreground max-w-xl text-lg leading-relaxed">
              {landingText.tagline}
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link to="/login" className="no-underline">
              <Button type="button">{landingText.signIn}</Button>
            </Link>
            <Link to="/app" className="no-underline">
              <Button type="button" variant="outline">
                {landingText.openApp}
              </Button>
            </Link>
          </div>
        </div>

        <div className="flex flex-col gap-5">
          <h2 className="text-xl font-semibold">{landingText.availableNowHeading}</h2>
          <div className="flex flex-wrap gap-4">
            {landingFeaturesAvailable.map((feature, index) => (
              <FeatureCard key={feature.title} feature={feature} icon={availableIcons[index]!} />
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-5">
          <h2 className="text-xl font-semibold">{landingText.upcomingFeaturesHeading}</h2>
          <div className="flex flex-wrap gap-4">
            {landingFeaturesUpcoming.map((feature, index) => (
              <FeatureCard
                key={feature.title}
                feature={feature}
                icon={upcomingIcons[index]!}
                upcoming
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
